import {convertToPercentile} from '../../../shared/shared-utils/number/number.utils';

/**
 * Calculates the quantity difference based on sales history, stock days, and growth factor.
 *
 * @param {number} nettSold - Total units sold over the historical period.
 * @param {number} monthsHistory - Number of months of historical data.
 * @param {number} stockDays - The number of days to project stock coverage for.
 * @param {number} growth - Growth factor expressed as a percentage (e.g., 10 for 10% growth).
 * @param {number} [daysInMonths=30] - The assumed number of days in each month.
 * @param {number} [daysZero=0] - Days with zero sales to be excluded.
 * @param {number} [daysNegative=0] - Days with negative sales to be excluded.
 * @returns {number} The adjusted quantity based on historical sales, growth, and target stock coverage.
 *
 * @example
 * calculateQuantityDifference(300, 3, 14, 10); // Calculates using 3 months of history, 14 days stock, 10% growth
 */
export function calculateQuantityDifference(
  nettSold: number,
  monthsHistory: number,
  stockDays: number,
  growth: number,
  daysZero: number = 0,
  daysNegative: number = 0,
  daysInMonths: number = 30,
): number {
  const growthPercentile = convertToPercentile(growth);
  const historyInDays = calculateMonthsHistory(monthsHistory, daysInMonths, daysZero, daysNegative);
  const nettDays = (nettSold / historyInDays);
  return nettDays * stockDays * growthPercentile;
}

/**
 * Calculates the total number of valid days in a given history window,
 * subtracting days with zero or negative sales.
 *
 * @param {number} monthsHistory - Number of months to include in history.
 * @param {number} daysInMonths - Assumed number of days per month.
 * @param {number} daysZero - Number of days with zero sales to exclude.
 * @param {number} daysNegative - Number of days with negative sales to exclude.
 * @returns {number} The effective number of days in the historical period.
 *
 * @example
 * calculateMonthsHistory(3, 30, 2, 1); // returns 87 (3*30 - 2 - 1)
 */
export function calculateMonthsHistory(
  monthsHistory: number,
  daysInMonths: number,
  daysZero: number,
  daysNegative: number,
): number {
  return (monthsHistory * daysInMonths) - daysZero - daysNegative;
}

/**
 * Rounds a suggested order quantity up to the nearest whole unit.
 */
export function roundSuggestedOrderQty(qty: number): number {
  return Math.ceil(qty);
}

export function calculateDataQuantityDifference<DataObjectType, DataAdditional, ReturnType>(
  dataObject: Record<string, DataObjectType>,
  additionalData: Record<string, DataAdditional>,
  propertyForMainSupp: string,
  propertyForQtyDiff: string,
  propertyForNew: string,
  propertyForPurchaseOrder: string,
  propertyForOnHand: string,
  propertyForOrderLevel: string,
  propertyForNettSold: string,
  monthsHistory: number,
  stockDays: number,
  growth: number,
  isMeetMinimumOrderLevelOn: boolean,
  isSubtractPurchaseOrderOn: boolean,
  qtyDiffDecimals: number = 2,
  daysInMonths: number = 30,
  daysZero: number = 0,
  daysNegative: number = 0,
): Record<string, ReturnType> {
  const data = {};
  for (const [key, value] of Object.entries(dataObject)) {
    if (value[propertyForMainSupp] === 1) {
      data[key] = {} as ReturnType;
      data[key][propertyForQtyDiff] = undefined;
    }

    let quantityDiff: number;

    if (value[propertyForNew] === true || value[propertyForNew] === 'ADDED') {
      quantityDiff = null;
    } else {
      const subPOMultiplier: 1 | 0 = isSubtractPurchaseOrderOn ? 1 : 0;
      const qtyPurchaseOrder = subPOMultiplier * +(additionalData?.[key]?.[propertyForPurchaseOrder] ?? 0);
      const qtyDifference = calculateQuantityDifference(
        +data[propertyForNettSold],
        monthsHistory,
        stockDays,
        growth,
        daysZero,
        daysNegative,
        daysInMonths,
      );
      const qtyDiffMinusOnHand = qtyDifference - +additionalData[key][propertyForOnHand] - qtyPurchaseOrder;

      if (isMeetMinimumOrderLevelOn) {
        const itemOrdLvl = +(additionalData[key]?.[propertyForOrderLevel] ?? 0);
        let suggestedQty = qtyDiffMinusOnHand;
        if (itemOrdLvl > 0 && qtyDiffMinusOnHand < itemOrdLvl) {
          suggestedQty = itemOrdLvl - +additionalData[key][propertyForOnHand] - qtyPurchaseOrder;
        }
        quantityDiff = Math.max(0, suggestedQty);
      } else {
        quantityDiff = qtyDiffMinusOnHand;
      }
    }

    if (quantityDiff !== null) {
      const numQtyDiff = Number(quantityDiff);
      if (!isNaN(numQtyDiff)) {
        data[key][propertyForQtyDiff] = String(roundSuggestedOrderQty(numQtyDiff));
      } else {
        data[key][propertyForQtyDiff] = 'NaN';
      }
    } else {
      data[key][propertyForQtyDiff] = 'NaN';
    }
  }
  return data;
}
